import { callAgroApi } from '@services/agroapi';
import { SupplyChainBrokerQuotaResponse, SupplyChainBrokerResponse, SupplyChainClimateRisk, SupplyChainClimateRiskResponse, SupplyChainCycleLengthResponse, SupplyChainDailyHarvestRecordResponse, SupplyChainDailyPlantingRecordResponse, SupplyChainDistrictResponse, SupplyChainEngineRecommendationResponse, SupplyChainEngineRunResponse, SupplyChainFarmerPerformanceResponse, SupplyChainHarvestGroupBy, SupplyChainHarvestProjectionResponse, SupplyChainHarvestStatsResponse, SupplyChainMaturityDayResponse, SupplyChainMonthlyTargetResponse, SupplyChainPlantingDateField, SupplyChainPlantingGroupBy, SupplyChainPlantingStatsResponse, SupplyChainSeasonResponse, SupplyChainStatsInterval } from '@services/responseTypes';
export type SupplyChainSeasonPayload = Partial<Omit<SupplyChainSeasonResponse, 'id' | 'organization_id'>>;
export type SupplyChainMonthlyTargetPayload = Partial<Omit<SupplyChainMonthlyTargetResponse, 'id' | 'organization_id'>>;
export type SupplyChainBrokerQuotaPayload = Partial<Omit<SupplyChainBrokerQuotaResponse, 'id' | 'organization_id'>>;
export type SupplyChainFarmerPerformanceFilters = {
  sowing_from?: string;
  sowing_to?: string;
  harvest_from?: string;
  harvest_to?: string;
  province?: string;
  district?: string;
  district_id?: string;
  agent_id?: string;
  broker_id?: string;
  is_valid?: string;
};
export type SupplyChainPlantingStatsParams = SupplyChainFarmerPerformanceFilters & {
  from: string;
  to: string;
  date_field: SupplyChainPlantingDateField;
  interval: SupplyChainStatsInterval;
  group_by: SupplyChainPlantingGroupBy;
};
export type SupplyChainHarvestStatsParams = {
  from: string;
  to: string;
  interval: SupplyChainStatsInterval;
  group_by: SupplyChainHarvestGroupBy;
  season_year?: number;
};
const toQueryString = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};
const PAGE_ITEMS = 500;
const MAX_PAGES = 100;
const pageCountFromHeaders = (headers: Record<string, unknown>) => {
  const totalPages = Number(headers['total-pages']);
  if (Number.isFinite(totalPages) && totalPages > 0) return totalPages;
  const totalCount = Number(headers['total-count']);
  if (headers['total-count'] !== undefined && Number.isFinite(totalCount)) {
    return Math.max(1, Math.ceil(totalCount / PAGE_ITEMS));
  }
  return null;
};
const fetchAllPages = <T,>(path: string, params: Record<string, string | number | undefined> = {}): Promise<T[]> => {
  const pageUrl = (page: number) => `${path}${toQueryString({
    ...params,
    items: PAGE_ITEMS,
    page
  })}`;
  const fetchRemaining = (page: number, collected: T[]): Promise<T[]> => page > MAX_PAGES ? Promise.resolve(collected) : callAgroApi<T[]>(pageUrl(page), 'GET').then(({
    data
  }) => data.length < PAGE_ITEMS ? collected.concat(data) : fetchRemaining(page + 1, collected.concat(data)));
  return callAgroApi<T[]>(pageUrl(1), 'GET').then(first => {
    if (first.data.length < PAGE_ITEMS) return first.data;
    const knownPages = pageCountFromHeaders(first.headers);
    if (knownPages === null) return fetchRemaining(2, first.data);
    const totalPages = Math.min(knownPages, MAX_PAGES);
    if (totalPages <= 1) return first.data;
    return Promise.all(Array.from({
      length: totalPages - 1
    }, (_, index) => callAgroApi<T[]>(pageUrl(index + 2), 'GET').then(({
      data
    }) => data))).then(rest => first.data.concat(...rest));
  });
};
export const withSupplyChain = self => ({
  actions: {
    fetchSupplyChainSeasons() {
      return callAgroApi<SupplyChainSeasonResponse[]>(`/organizations/${self.id}/supply_chain/seasons`, 'GET').then(({
        data
      }) => data);
    },
    createSupplyChainSeason(season: SupplyChainSeasonPayload) {
      return callAgroApi<SupplyChainSeasonResponse>(`/organizations/${self.id}/supply_chain/seasons`, 'POST', season).then(({
        data
      }) => data);
    },
    updateSupplyChainSeason(id: string, season: SupplyChainSeasonPayload) {
      return callAgroApi<SupplyChainSeasonResponse>(`/organizations/${self.id}/supply_chain/seasons/${id}`, 'PATCH', season).then(({
        data
      }) => data);
    },
    fetchSupplyChainMonthlyTargets(seasonId: string) {
      return callAgroApi<SupplyChainMonthlyTargetResponse[]>(`/organizations/${self.id}/supply_chain/monthly_targets${toQueryString({
        season_id: seasonId
      })}`, 'GET').then(({
        data
      }) => data);
    },
    createSupplyChainMonthlyTarget(target: SupplyChainMonthlyTargetPayload) {
      return callAgroApi<SupplyChainMonthlyTargetResponse>(`/organizations/${self.id}/supply_chain/monthly_targets`, 'POST', target).then(({
        data
      }) => data);
    },
    updateSupplyChainMonthlyTarget(id: string, target: SupplyChainMonthlyTargetPayload) {
      return callAgroApi<SupplyChainMonthlyTargetResponse>(`/organizations/${self.id}/supply_chain/monthly_targets/${id}`, 'PATCH', target).then(({
        data
      }) => data);
    },
    fetchSupplyChainBrokerQuotas(params: {
      season_id: string;
      month?: number;
      broker_id?: string;
    }) {
      return fetchAllPages<SupplyChainBrokerQuotaResponse>(`/organizations/${self.id}/supply_chain/broker_quotas`, params);
    },
    createSupplyChainBrokerQuota(quota: SupplyChainBrokerQuotaPayload) {
      return callAgroApi<SupplyChainBrokerQuotaResponse>(`/organizations/${self.id}/supply_chain/broker_quotas`, 'POST', quota).then(({
        data
      }) => data);
    },
    updateSupplyChainBrokerQuota(id: string, quota: SupplyChainBrokerQuotaPayload) {
      return callAgroApi<SupplyChainBrokerQuotaResponse>(`/organizations/${self.id}/supply_chain/broker_quotas/${id}`, 'PATCH', quota).then(({
        data
      }) => data);
    },
    fetchSupplyChainBrokers() {
      return fetchAllPages<SupplyChainBrokerResponse>(`/organizations/${self.id}/supply_chain/brokers`);
    },
    fetchSupplyChainDistricts() {
      return fetchAllPages<SupplyChainDistrictResponse>(`/organizations/${self.id}/supply_chain/districts`);
    },
    fetchSupplyChainCycleLengths(params: {
      district_id?: string;
      planting_month?: number;
    } = {}) {
      return fetchAllPages<SupplyChainCycleLengthResponse>(`/organizations/${self.id}/supply_chain/cycle_lengths`, params);
    },
    fetchSupplyChainMaturityDays(params: {
      district_id?: string;
      planting_mm_dd?: string;
      planting_doy?: number;
    } = {}) {
      return fetchAllPages<SupplyChainMaturityDayResponse>(`/organizations/${self.id}/supply_chain/maturity_days`, params);
    },
    fetchSupplyChainFarmerPerformances(params: SupplyChainFarmerPerformanceFilters = {}) {
      return fetchAllPages<SupplyChainFarmerPerformanceResponse>(`/organizations/${self.id}/supply_chain/farmer_performances`, params);
    },
    fetchSupplyChainClimateRisks(params: {
      from?: string;
      to?: string;
      district_id?: string;
      province?: string;
      risk?: SupplyChainClimateRisk;
      min_level?: number;
    } = {}) {
      return fetchAllPages<SupplyChainClimateRiskResponse>(`/organizations/${self.id}/supply_chain/climate_risks`, params);
    },
    fetchSupplyChainPlantingStats(params: SupplyChainPlantingStatsParams) {
      return callAgroApi<SupplyChainPlantingStatsResponse>(`/organizations/${self.id}/supply_chain/planting_stats${toQueryString(params)}`, 'GET').then(({
        data
      }) => data);
    },
    fetchSupplyChainHarvestStats(params: SupplyChainHarvestStatsParams) {
      return callAgroApi<SupplyChainHarvestStatsResponse>(`/organizations/${self.id}/supply_chain/harvest_stats${toQueryString(params)}`, 'GET').then(({
        data
      }) => data);
    },
    fetchSupplyChainDailyPlantingRecords(params: {
      broker_id?: string;
      district_id?: string;
      season_year?: number;
      is_valid?: string;
      from?: string;
      to?: string;
    } = {}) {
      return fetchAllPages<SupplyChainDailyPlantingRecordResponse>(`/organizations/${self.id}/supply_chain/daily_planting_records`, params);
    },
    fetchSupplyChainDailyHarvestRecords(params: {
      broker_id?: string;
      is_valid?: string;
      from?: string;
      to?: string;
    } = {}) {
      return fetchAllPages<SupplyChainDailyHarvestRecordResponse>(`/organizations/${self.id}/supply_chain/daily_harvest_records`, params);
    },
    fetchSupplyChainHarvestProjections(params: {
      broker_id?: string;
      season_year?: number;
      from?: string;
      to?: string;
    } = {}) {
      return fetchAllPages<SupplyChainHarvestProjectionResponse>(`/organizations/${self.id}/supply_chain/harvest_projections`, params);
    },
    fetchSupplyChainEngineRuns(params: {
      as_of_date?: string;
    } = {}) {
      return callAgroApi<SupplyChainEngineRunResponse[]>(`/organizations/${self.id}/supply_chain/engine_runs${toQueryString(params)}`, 'GET').then(({
        data
      }) => data);
    },
    fetchSupplyChainEngineRecommendations(params: {
      engine_run_id?: string;
      lane?: string;
      outcome_status?: string;
    } = {}) {
      return fetchAllPages<SupplyChainEngineRecommendationResponse>(`/organizations/${self.id}/supply_chain/engine_recommendations`, params);
    },
    updateSupplyChainEngineRecommendation(id: string, outcome: {
      outcome_status: 'accepted' | 'rejected';
      outcome_actual_tons?: number;
    }) {
      return callAgroApi<SupplyChainEngineRecommendationResponse>(`/organizations/${self.id}/supply_chain/engine_recommendations/${id}`, 'PATCH', outcome).then(({
        data
      }) => data);
    }
  }
});